import { Quantity } from '@webapp/components/Quantity'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CurrencyName } from '@webapp/models/enums'
import messages from './messages'
import { AmountFractionStyled } from './styles/AmountFractionStyled'
import { AmountFractionType } from './types'

/*
 * Although the `mdp` prop (Multiple Decimal Places) is enabled for the Quantity component,
 * this constant enforces a fixed number of decimal places for quantities only.
 * It overrides the value that would otherwise be conditionally selected by the Quantity component
 * based on the `multiple_decimals_quantity` feature switch, ensuring consistency in how
 * quantities are displayed in this context.
 */
const DEFAULT_DECIMAL_PLACES = 5

interface BaseProps {
  amount: number
  totalAmount: number
  ariaLabelledBy?: string
  inline?: boolean
  size?: 'normal' | 'small'
}

interface CostProps extends BaseProps {
  type: typeof AmountFractionType.COST
  currencyName: CurrencyName
}

interface QuantityProps extends BaseProps {
  type: typeof AmountFractionType.QUANTITY
  decimalPlaces?: number
}

export type AmountFractionProps = CostProps | QuantityProps

export const AmountFraction = (props: AmountFractionProps) => {
  const intl = useProcurifyIntl()
  const {
    type,
    amount,
    totalAmount,
    ariaLabelledBy = undefined,
    inline = false,
    size = 'normal',
  } = props

  const renderAmountValue = (value: number) => {
    return type === AmountFractionType.COST ? (
      <Price inline price={value} currency={props.currencyName} />
    ) : (
      <Quantity
        mdp
        quantity={value}
        decimalPlaces={props.decimalPlaces ?? DEFAULT_DECIMAL_PLACES}
      />
    )
  }

  const getAriaLabel = () =>
    intl.formatMessage(
      type === AmountFractionType.COST
        ? messages.costAmountFractionAriaLabel
        : messages.quantityAmountFractionAriaLabel
    )

  const ariaProps = ariaLabelledBy
    ? { 'aria-labelledby': ariaLabelledBy }
    : { 'aria-label': getAriaLabel() }

  return (
    <AmountFractionStyled
      {...ariaProps}
      as={inline ? 'span' : 'div'}
      size={size}
    >
      <span className='numerator'>{renderAmountValue(amount)}</span>
      <span className='separator'>{' / '}</span>
      <span className='denominator'>{renderAmountValue(totalAmount)}</span>
    </AmountFractionStyled>
  )
}
